import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, X, DownloadSimple, PencilSimple, Plus, Books, Info,
  MagnifyingGlass, Funnel, CaretDown, CaretUp, Shield, Warning,
  Clock, User, Buildings, CalendarCheck, FileText, ArrowRight,
  Eye, CheckCircle, XCircle, ListBullets, GridFour, GitBranch, Trash,
  Lightbulb, Sparkle
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

// Theme colors and icons mapping
const THEME_CONFIG = {
  'Financial Services Regulation': { color: 'gold', icon: Buildings },
  'Data Protection': { color: 'purple', icon: Shield },
  'Financial Crime': { color: 'red', icon: Warning },
  'Corporate Governance': { color: 'blue', icon: Buildings },
  'Consumer Protection': { color: 'green', icon: User },
  'Employment & Labour': { color: 'orange', icon: User },
  'Tax Compliance': { color: 'yellow', icon: FileText },
  'Cybersecurity': { color: 'cyan', icon: Shield },
  'Banking Regulation': { color: 'emerald', icon: Buildings },
  'Insurance Regulation': { color: 'indigo', icon: Shield },
  'Investment Management': { color: 'violet', icon: Buildings },
  'Retirement Funds': { color: 'amber', icon: User },
  'Crypto Assets': { color: 'pink', icon: GitBranch },
  'Payment Systems': { color: 'teal', icon: Buildings },
};

const getThemeStyle = (theme) => {
  const config = THEME_CONFIG[theme] || { color: 'gray', icon: FileText };
  return config;
};

// Risk Badge with animated gradient
const RiskBadge = ({ level, size = 'sm' }) => {
  const styles = {
    High: 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-red-500/20',
    Medium: 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black shadow-amber-500/20',
    Low: 'bg-gradient-to-r from-emerald-600 to-green-500 text-white shadow-emerald-500/20'
  };
  const sizeClasses = size === 'lg' ? 'px-4 py-2 text-sm' : 'px-2 py-1 text-xs';
  
  return (
    <span className={`${sizeClasses} rounded-full font-semibold shadow-lg ${styles[level]}`}>
      {level}
    </span>
  );
};

// Animated Score Ring
const ScoreRing = ({ score, maxScore = 25, size = 60 }) => {
  const percentage = (score / maxScore) * 100;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const color = score >= 15 ? '#ef4444' : score >= 8 ? '#f59e0b' : '#22c55e';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#1a1a1a" strokeWidth={strokeWidth} />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-mono font-bold text-text-primary">{score}</span>
      </div>
    </div>
  );
};

// Version Badge
const VersionBadge = ({ version }) => (
  <span className="px-2 py-1 bg-gold/10 border border-gold/30 text-gold text-xs font-mono rounded-full">
    v{version}
  </span>
);

// Approval Status Badge
const ApprovalBadge = ({ approved, approvalDate }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
    approved 
      ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
      : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
  }`}>
    {approved ? <CheckCircle size={16} /> : <Clock size={16} />}
    <span>{approved ? `Approved ${approvalDate || ''}` : 'Pending Approval'}</span>
  </div>
);

// Version History Panel Component
const VersionHistoryPanel = ({ universe, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  const versionHistory = universe?.version_history || [];
  const hasHistory = versionHistory.length > 0;
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed top-0 right-0 h-full w-full max-w-md bg-background border-l border-border shadow-2xl z-50 overflow-hidden"
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border bg-background-surface">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-heading font-semibold text-text-primary flex items-center gap-2">
              <GitBranch size={24} className="text-gold" />
              Version History
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-background rounded-lg text-text-tertiary"
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-text-tertiary">
            Track all changes and approvals to your regulatory universe
          </p>
        </div>
        
        {/* Current Version Info */}
        <div className="p-4 mx-4 mt-4 bg-gold/10 border border-gold/30 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <span className="text-gold font-mono font-bold">v{universe?.version || 1}</span>
            </div>
            <div>
              <div className="text-sm font-medium text-text-primary">Current Version</div>
              <div className="text-xs text-text-tertiary">
                {universe?.approved ? 'Approved' : 'Pending Approval'}
              </div>
            </div>
          </div>
          
          {universe?.approved && (
            <div className="mt-3 pt-3 border-t border-gold/20 space-y-1 text-xs">
              <div className="flex justify-between text-text-tertiary">
                <span>Approved by:</span>
                <span className="text-text-primary">{universe?.approved_by_name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between text-text-tertiary">
                <span>Committee:</span>
                <span className="text-text-primary">{universe?.approving_committee || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-text-tertiary">
                <span>Date:</span>
                <span className="text-text-primary">
                  {universe?.approval_date ? new Date(universe.approval_date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Version History Timeline */}
        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-sm font-medium text-text-tertiary mb-4 uppercase tracking-wider">
            Change Log
          </h3>
          
          {hasHistory ? (
            <div className="space-y-4">
              {versionHistory.slice().reverse().map((entry, idx) => (
                <div key={idx} className="relative pl-6">
                  {/* Timeline connector */}
                  {idx < versionHistory.length - 1 && (
                    <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-border" />
                  )}
                  
                  {/* Timeline dot */}
                  <div className={`absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center ${
                    idx === 0 ? 'bg-gold' : 'bg-background-surface border border-border'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-gold-foreground' : 'bg-text-tertiary'}`} />
                  </div>
                  
                  <div className="bg-background-surface border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm text-gold">v{entry.version}</span>
                      <span className="text-xs text-text-tertiary">
                        {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                    <p className="text-sm text-text-primary mb-2">{entry.changes}</p>
                    <div className="flex items-center gap-2 text-xs text-text-tertiary">
                      <User size={12} />
                      <span>{entry.changed_by || 'System'}</span>
                      {entry.regulation_count !== undefined && (
                        <>
                          <span>•</span>
                          <span>{entry.regulation_count} regulations</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock size={32} className="mx-auto mb-2 text-text-tertiary opacity-50" />
              <p className="text-text-tertiary text-sm">No version history yet</p>
              <p className="text-text-tertiary text-xs mt-1">Changes will appear here as you edit</p>
            </div>
          )}
        </div>
        
        {/* Audit Trail Section */}
        <div className="p-4 border-t border-border bg-background-surface">
          <h3 className="text-sm font-medium text-text-tertiary mb-3 uppercase tracking-wider">
            Audit Information
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-text-tertiary">Created:</span>
              <span className="text-text-primary">
                {universe?.created_at ? new Date(universe.created_at).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-tertiary">Last Modified:</span>
              <span className="text-text-primary">
                {universe?.last_modified ? new Date(universe.last_modified).toLocaleString() : 'Never'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-tertiary">Modified By:</span>
              <span className="text-text-primary">{universe?.last_modified_by || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-tertiary">Source:</span>
              <span className="text-text-primary capitalize">{universe?.source || 'Generated'}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Regulation Card - Modern Design
const RegulationCard = ({ reg, onToggle, onAdjust, onSelect, isSelected }) => {
  const themeStyle = getThemeStyle(reg.theme);
  const ThemeIcon = themeStyle.icon;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      onClick={() => onSelect(reg)}
      className={`group relative bg-background-surface border rounded-lg overflow-hidden transition-all duration-300 cursor-pointer ${
        reg.selected 
          ? 'border-gold/50 shadow-lg shadow-gold/5' 
          : 'border-border/50 opacity-70 hover:opacity-100'
      }`}
    >
      {/* Theme indicator bar */}
      <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-${themeStyle.color}-500 to-${themeStyle.color}-600`} />
      
      <div className="p-5 pl-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-semibold text-text-primary truncate">{reg.name}</h3>
              {reg.user_adjusted && (
                <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full font-medium">
                  Adjusted
                </span>
              )}
              {reg.user_added && (
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-medium">
                  Custom
                </span>
              )}
              {reg.from_library && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full font-medium">
                  Library
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <ThemeIcon size={14} className={`text-${themeStyle.color}-400`} />
              <span>{reg.theme}</span>
              {reg.regulator && (
                <>
                  <span className="text-border">•</span>
                  <span>{reg.regulator}</span>
                </>
              )}
            </div>
          </div>
          
          {/* Actions - Large, Clear Buttons */}
          <div className="flex items-center gap-3 relative z-10">
            {/* Edit Risk Rating Button */}
            <button
              onClick={(e) => { e.stopPropagation(); onAdjust(reg); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold text-gold-foreground font-medium text-sm hover:bg-gold/90 transition-all shadow-md"
              aria-label="Edit risk rating"
            >
              <PencilSimple size={18} weight="bold" />
              <span>Edit</span>
            </button>
            
            {/* Remove Button */}
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(reg.id, reg.selected); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500 text-white font-medium text-sm hover:bg-red-600 transition-all shadow-md"
              aria-label="Remove from universe"
            >
              <Trash size={18} weight="bold" />
              <span>Remove</span>
            </button>
          </div>
        </div>
        
        {/* Risk Metrics */}
        <div className="flex items-center justify-between pt-3 border-t border-border/50">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <ScoreRing score={reg.risk_score} size={40} />
              <div className="text-xs">
                <div className="text-text-tertiary">Risk Score</div>
                <div className="text-text-primary font-medium">{reg.impact}×{reg.probability}</div>
              </div>
            </div>
          </div>
          <RiskBadge level={reg.risk_level} />
        </div>
      </div>
    </motion.div>
  );
};

// Legislation Library Browser Modal
const LegislationLibraryModal = ({ isOpen, onClose, onAddLegislation, existingNames }) => {
  const [legislation, setLegislation] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedThemes, setExpandedThemes] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchLibrary();
    }
  }, [isOpen]);

  const fetchLibrary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/regulations/library`);
      setLegislation(response.data.regulations || []);
      setGrouped(response.data.grouped || {});
      const themes = Object.keys(response.data.grouped || {});
      if (themes.length > 0) {
        setExpandedThemes({ [themes[0]]: true });
      }
    } catch (error) {
      toast.error('Failed to load legislation library');
    } finally {
      setLoading(false);
    }
  };

  const filteredGrouped = useMemo(() => {
    if (!search && !selectedTheme) return grouped;
    
    const result = {};
    const searchLower = search.toLowerCase();
    
    Object.entries(grouped).forEach(([theme, items]) => {
      if (selectedTheme && theme !== selectedTheme) return;
      
      const filtered = items.filter(item => {
        if (existingNames.includes(item.name)) return false;
        if (!search) return true;
        return item.name.toLowerCase().includes(searchLower) ||
               item.description?.toLowerCase().includes(searchLower) ||
               item.regulator?.toLowerCase().includes(searchLower);
      });
      
      if (filtered.length > 0) {
        result[theme] = filtered;
      }
    });
    
    return result;
  }, [grouped, search, selectedTheme, existingNames]);

  const toggleTheme = (theme) => {
    setExpandedThemes(prev => ({ ...prev, [theme]: !prev[theme] }));
  };

  const toggleSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    );
  };

  const handleAddSelected = () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    onAddLegislation(selectedItems);
    setSelectedItems([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background border border-border rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-border bg-background-surface">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary flex items-center gap-2">
                <Books size={24} className="text-gold" />
                Legislation Library
              </h2>
              <p className="text-sm text-text-tertiary mt-1">
                Browse and add legislation from the South African regulatory framework
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-background rounded-lg transition-colors">
              <X size={24} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search legislation..."
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
              />
            </div>
            <select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              className="px-4 py-2.5 bg-background border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none min-w-[200px]"
            >
              <option value="">All Themes</option>
              {Object.keys(grouped).map(theme => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gold font-mono">Loading library...</div>
            </div>
          ) : Object.keys(filteredGrouped).length === 0 ? (
            <div className="text-center py-12 text-text-tertiary">
              <Books size={48} className="mx-auto mb-4 opacity-50" />
              <p>No legislation found matching your criteria</p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(filteredGrouped).map(([theme, items]) => {
                const themeStyle = getThemeStyle(theme);
                const ThemeIcon = themeStyle.icon;
                const isExpanded = expandedThemes[theme];
                
                return (
                  <div key={theme} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleTheme(theme)}
                      className="w-full flex items-center justify-between p-4 bg-background-surface hover:bg-background transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <ThemeIcon size={20} className={`text-${themeStyle.color}-400`} />
                        <span className="font-medium text-text-primary">{theme}</span>
                        <span className="px-2 py-0.5 bg-background rounded-full text-xs text-text-tertiary">
                          {items.length}
                        </span>
                      </div>
                      {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
                    </button>
                    
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-2 space-y-1 bg-background">
                            {items.map(item => {
                              const isSelected = selectedItems.includes(item.id);
                              return (
                                <div
                                  key={item.id}
                                  onClick={() => toggleSelection(item.id)}
                                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'bg-gold/10 border border-gold/30' 
                                      : 'hover:bg-background-surface border border-transparent'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-all ${
                                      isSelected 
                                        ? 'bg-gold border-gold' 
                                        : 'border-border'
                                    }`}>
                                      {isSelected && <Check size={14} className="text-gold-foreground" weight="bold" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-text-primary text-sm">{item.name}</h4>
                                      <p className="text-xs text-text-tertiary mt-0.5 line-clamp-2">{item.description}</p>
                                      <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
                                        <span>{item.regulator}</span>
                                        <span className="text-border">•</span>
                                        <span>Impact: {item.default_impact}/5</span>
                                        <span className="text-border">•</span>
                                        <span>Probability: {item.default_probability}/5</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-border bg-background-surface flex items-center justify-between">
          <div className="text-sm text-text-tertiary">
            {selectedItems.length} legislation{selectedItems.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-text-primary hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelected}
              disabled={selectedItems.length === 0}
              className="px-4 py-2 bg-gold text-gold-foreground rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus size={18} />
              Add Selected
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Frameworks Library Browser Modal
const FrameworksLibraryModal = ({ isOpen, onClose, onAddFrameworks, existingNames }) => {
  const [frameworks, setFrameworks] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchFrameworks();
    }
  }, [isOpen]);

  const fetchFrameworks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/frameworks/library`);
      setFrameworks(response.data.frameworks || []);
      setGrouped(response.data.grouped || {});
      const categories = Object.keys(response.data.grouped || {});
      if (categories.length > 0) {
        setExpandedCategories({ [categories[0]]: true });
      }
    } catch (error) {
      // Framework library might be empty - that's okay
      console.log('Frameworks library not yet populated');
    } finally {
      setLoading(false);
    }
  };

  const filteredGrouped = useMemo(() => {
    if (!search && !selectedCategory) return grouped;
    
    const result = {};
    const searchLower = search.toLowerCase();
    
    Object.entries(grouped).forEach(([category, items]) => {
      if (selectedCategory && category !== selectedCategory) return;
      
      const filtered = items.filter(item => {
        if (existingNames.includes(item.name)) return false;
        if (!search) return true;
        return item.name.toLowerCase().includes(searchLower) ||
               item.description?.toLowerCase().includes(searchLower);
      });
      
      if (filtered.length > 0) {
        result[category] = filtered;
      }
    });
    
    return result;
  }, [grouped, search, selectedCategory, existingNames]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const toggleSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    );
  };

  const handleAddSelected = () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one framework');
      return;
    }
    onAddFrameworks(selectedItems);
    setSelectedItems([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background border border-border rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-border bg-background-surface">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary flex items-center gap-2">
                <GitBranch size={24} className="text-emerald-400" />
                Frameworks Library
              </h2>
              <p className="text-sm text-text-tertiary mt-1">
                Browse and add compliance frameworks to your regulatory universe
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-background rounded-lg transition-colors">
              <X size={24} className="text-text-tertiary" />
            </button>
          </div>
          
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search frameworks..."
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2.5 bg-background border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none min-w-[200px]"
            >
              <option value="">All Categories</option>
              {Object.keys(grouped).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gold font-mono">Loading frameworks...</div>
            </div>
          ) : Object.keys(filteredGrouped).length === 0 ? (
            <div className="text-center py-12 text-text-tertiary">
              <GitBranch size={48} className="mx-auto mb-4 opacity-50" />
              <p className="mb-2">No frameworks available yet</p>
              <p className="text-sm">Frameworks will be added to the Master Library soon.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(filteredGrouped).map(([category, items]) => {
                const isExpanded = expandedCategories[category];
                
                return (
                  <div key={category} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-4 bg-background-surface hover:bg-background transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <GitBranch size={20} className="text-emerald-400" />
                        <span className="font-medium text-text-primary">{category}</span>
                        <span className="px-2 py-0.5 bg-background rounded-full text-xs text-text-tertiary">
                          {items.length}
                        </span>
                      </div>
                      {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
                    </button>
                    
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-2 space-y-1 bg-background">
                            {items.map(item => {
                              const isSelected = selectedItems.includes(item.id);
                              return (
                                <div
                                  key={item.id}
                                  onClick={() => toggleSelection(item.id)}
                                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                      : 'hover:bg-background-surface border border-transparent'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-all ${
                                      isSelected 
                                        ? 'bg-emerald-500 border-emerald-500' 
                                        : 'border-border'
                                    }`}>
                                      {isSelected && <Check size={14} className="text-white" weight="bold" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-text-primary text-sm">{item.name}</h4>
                                      <p className="text-xs text-text-tertiary mt-0.5 line-clamp-2">{item.description}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-border bg-background-surface flex items-center justify-between">
          <div className="text-sm text-text-tertiary">
            {selectedItems.length} framework{selectedItems.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-text-primary hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelected}
              disabled={selectedItems.length === 0}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus size={18} />
              Add Selected
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Approval Modal
const ApprovalModal = ({ isOpen, onClose, onApprove }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    owner_name: '',
    owner_surname: '',
    owner_email: user?.email || '',
    owner_department: '',
    owner_title: '',
    approval_date: new Date().toISOString().split('T')[0],
    approving_committee: '',
    approval_notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.owner_name || !formData.owner_surname || !formData.owner_email) {
      toast.error('Please fill in owner details');
      return;
    }
    if (!formData.approval_date || !formData.approving_committee) {
      toast.error('Please fill in approval date and committee');
      return;
    }
    
    setLoading(true);
    try {
      await onApprove(formData);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background border border-border rounded-lg w-full max-w-2xl"
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-text-primary flex items-center gap-2">
            <CalendarCheck size={24} className="text-gold" />
            Approve Regulatory Universe
          </h2>
          <p className="text-sm text-text-tertiary mt-1">
            Complete the approval details for audit trail compliance
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Owner Information */}
          <div>
            <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
              <User size={16} />
              Owner Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-tertiary mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1">Surname *</label>
                <input
                  type="text"
                  value={formData.owner_surname}
                  onChange={(e) => setFormData({ ...formData, owner_surname: e.target.value })}
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1">Department *</label>
                <input
                  type="text"
                  value={formData.owner_department}
                  onChange={(e) => setFormData({ ...formData, owner_department: e.target.value })}
                  placeholder="e.g., Compliance, Legal"
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-text-tertiary mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.owner_title}
                  onChange={(e) => setFormData({ ...formData, owner_title: e.target.value })}
                  placeholder="e.g., Chief Compliance Officer, Compliance Manager"
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
            </div>
          </div>
          
          {/* Approval Details */}
          <div>
            <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
              <CalendarCheck size={16} />
              Approval Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-tertiary mb-1">Approval Date *</label>
                <input
                  type="date"
                  value={formData.approval_date}
                  onChange={(e) => setFormData({ ...formData, approval_date: e.target.value })}
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1">Approving Committee *</label>
                <input
                  type="text"
                  value={formData.approving_committee}
                  onChange={(e) => setFormData({ ...formData, approving_committee: e.target.value })}
                  placeholder="e.g., Risk Committee, Board"
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-text-tertiary mb-1">Approval Notes</label>
                <textarea
                  value={formData.approval_notes}
                  onChange={(e) => setFormData({ ...formData, approval_notes: e.target.value })}
                  placeholder="Optional notes about the approval..."
                  rows={2}
                  className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>
          
          {/* Info Box */}
          <div className="p-4 bg-gold/5 border border-gold/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-gold flex-shrink-0 mt-0.5" />
              <div className="text-sm text-text-secondary">
                <p className="font-medium text-gold mb-1">Audit Trail Notice</p>
                <p className="text-xs text-text-tertiary">
                  This approval will be timestamped and recorded for audit purposes. 
                  The approved universe can be downloaded as a PDF with all audit trail details.
                </p>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-border rounded-lg text-text-secondary hover:bg-background-surface transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gold text-gold-foreground rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? 'Approving...' : (
                <>
                  <Check size={18} weight="bold" />
                  Approve Universe
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Risk Adjustment Modal
const RiskAdjustmentModal = ({ regulation, onClose, onSave }) => {
  const [impact, setImpact] = useState(regulation?.impact || 3);
  const [probability, setProbability] = useState(regulation?.probability || 3);
  const [rationale, setRationale] = useState('');
  const [loading, setLoading] = useState(false);

  const newScore = impact * probability;
  const newLevel = newScore >= 15 ? 'High' : newScore >= 8 ? 'Medium' : 'Low';

  const handleSave = async () => {
    if (!rationale.trim()) {
      toast.error('Please provide a rationale for the adjustment');
      return;
    }
    
    setLoading(true);
    try {
      await onSave({
        new_impact: impact,
        new_probability: probability,
        rationale
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!regulation) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background border border-border rounded-lg w-full max-w-lg"
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Adjust Risk Rating</h2>
          <p className="text-sm text-text-tertiary mt-1">{regulation.name}</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Current vs New */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-background-surface rounded-lg border border-border">
              <div className="text-xs text-text-tertiary mb-2">Current Rating</div>
              <div className="flex items-center gap-3">
                <ScoreRing score={regulation.risk_score} size={50} />
                <div>
                  <div className="font-mono text-text-primary">{regulation.impact}×{regulation.probability}</div>
                  <RiskBadge level={regulation.risk_level} />
                </div>
              </div>
            </div>
            <div className="p-4 bg-gold/5 rounded-lg border border-gold/30">
              <div className="text-xs text-gold mb-2">New Rating</div>
              <div className="flex items-center gap-3">
                <ScoreRing score={newScore} size={50} />
                <div>
                  <div className="font-mono text-text-primary">{impact}×{probability}</div>
                  <RiskBadge level={newLevel} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Sliders */}
          <div className="space-y-4">
            <div>
              <label className="flex items-center justify-between text-sm mb-2">
                <span className="text-text-secondary">Impact</span>
                <span className="font-mono text-gold">{impact}/5</span>
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={impact}
                onChange={(e) => setImpact(parseInt(e.target.value))}
                className="w-full accent-gold"
              />
            </div>
            <div>
              <label className="flex items-center justify-between text-sm mb-2">
                <span className="text-text-secondary">Probability</span>
                <span className="font-mono text-gold">{probability}/5</span>
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={probability}
                onChange={(e) => setProbability(parseInt(e.target.value))}
                className="w-full accent-gold"
              />
            </div>
          </div>
          
          {/* Rationale */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">Rationale *</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain the reason for this risk adjustment..."
              rows={3}
              className="w-full px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none resize-none"
              required
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-border rounded-lg text-text-secondary hover:bg-background-surface transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 py-2.5 bg-gold text-gold-foreground rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Adjustment'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Detail Panel with Actions
const DetailPanel = ({ regulation, onClose, onEditRating, onRemove, isApproved }) => {
  if (!regulation) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="bg-background border border-border rounded-lg w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-1">{regulation.name}</h2>
              <p className="text-sm text-text-tertiary">{regulation.regulator}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-background-surface rounded-lg">
              <X size={20} className="text-text-tertiary" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-2">Description</h3>
            <p className="text-text-primary">{regulation.description || 'No description available.'}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-2">Theme</h3>
              <p className="text-text-primary">{regulation.theme}</p>
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-2">Category</h3>
              <p className="text-text-primary capitalize">{regulation.category}</p>
            </div>
          </div>
          
          <div>
            <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-3">Risk Assessment</h3>
            <div className="flex items-center gap-8 p-4 bg-background-surface rounded-lg">
              <ScoreRing score={regulation.risk_score} size={70} />
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <span className="text-text-tertiary text-sm w-24">Impact:</span>
                  <span className="font-mono text-text-primary">{regulation.impact}/5</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-text-tertiary text-sm w-24">Probability:</span>
                  <span className="font-mono text-text-primary">{regulation.probability}/5</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-text-tertiary text-sm w-24">Risk Level:</span>
                  <RiskBadge level={regulation.risk_level} size="lg" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          {!isApproved && (
            <div className="flex gap-3 pt-4 border-t border-border">
              <button
                onClick={() => {
                  onClose();
                  onEditRating(regulation);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-gold/10 border border-gold/30 text-gold rounded-lg hover:bg-gold/20 transition-colors font-medium"
                data-testid="detail-edit-rating-btn"
              >
                <PencilSimple size={18} />
                Edit Risk Rating
              </button>
              <button
                onClick={() => {
                  onRemove(regulation.id);
                  onClose();
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors font-medium"
                data-testid="detail-remove-btn"
              >
                <Trash size={18} />
                Remove from Universe
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Main Component
const RegulatoryUniverse = () => {
  const { universeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [universe, setUniverse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReg, setSelectedReg] = useState(null);
  const [adjustingReg, setAdjustingReg] = useState(null);
  const [showLegislationLibrary, setShowLegislationLibrary] = useState(false);
  const [showFrameworksLibrary, setShowFrameworksLibrary] = useState(false);
  const [showApproval, setShowApproval] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTheme, setFilterTheme] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  useEffect(() => {
    fetchUniverse();
  }, [universeId]);

  const fetchUniverse = async () => {
    try {
      const response = await axios.get(`${API}/universe/${universeId}`);
      setUniverse(response.data);
    } catch (error) {
      toast.error('Failed to load regulatory universe');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelection = async (regulationId, currentStatus) => {
    try {
      await axios.put(`${API}/universe/${universeId}/validate`, {
        regulation_id: regulationId,
        selected: !currentStatus
      });
      setUniverse(prev => ({
        ...prev,
        regulations: prev.regulations.map(r =>
          r.id === regulationId ? { ...r, selected: !currentStatus } : r
        )
      }));
      toast.success('Selection updated');
    } catch (error) {
      toast.error('Failed to update selection');
    }
  };

  const handleSaveAdjustment = async (adjustmentData) => {
    try {
      await axios.put(`${API}/universe/${universeId}/adjust-risk`, {
        regulation_id: adjustingReg.id,
        ...adjustmentData
      });
      setUniverse(prev => ({
        ...prev,
        regulations: prev.regulations.map(r =>
          r.id === adjustingReg.id
            ? {
                ...r,
                impact: adjustmentData.new_impact,
                probability: adjustmentData.new_probability,
                risk_score: adjustmentData.new_impact * adjustmentData.new_probability,
                risk_level: adjustmentData.new_impact * adjustmentData.new_probability >= 15 
                  ? 'High' 
                  : adjustmentData.new_impact * adjustmentData.new_probability >= 8 
                  ? 'Medium' 
                  : 'Low',
                user_adjusted: true
              }
            : r
        )
      }));
      setAdjustingReg(null);
      toast.success('Risk rating adjusted');
    } catch (error) {
      toast.error('Failed to adjust risk rating');
    }
  };

  const handleAddFromLibrary = async (regulationIds) => {
    try {
      const response = await axios.post(`${API}/universe/${universeId}/add-from-library`, regulationIds);
      if (response.data.added > 0) {
        toast.success(`Added ${response.data.added} regulation(s) to your universe`);
        fetchUniverse();
      } else {
        toast.info('All selected regulations already exist in your universe');
      }
    } catch (error) {
      toast.error('Failed to add regulations');
    }
  };

  const handleApprove = async (approvalData) => {
    try {
      await axios.put(`${API}/universe/${universeId}/approve`, approvalData);
      toast.success('Regulatory universe approved!');
      setShowApproval(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to approve universe');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Generating PDF...');
      const response = await axios.get(`${API}/universe/${universeId}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `regulatory_universe_v${universe?.version || 1}_${universeId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.dismiss();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate PDF');
    }
  };

  const handleRemoveRegulation = async (regulationId) => {
    try {
      // Remove by setting selected to false and filtering out
      await axios.put(`${API}/universe/${universeId}/validate`, {
        regulation_id: regulationId,
        selected: false
      });
      setUniverse(prev => ({
        ...prev,
        regulations: prev.regulations.filter(r => r.id !== regulationId)
      }));
      toast.success('Regulation removed from your universe');
    } catch (error) {
      toast.error('Failed to remove regulation');
    }
  };

  // Filtering
  const filteredRegulations = useMemo(() => {
    if (!universe?.regulations) return [];
    
    return universe.regulations.filter(reg => {
      if (showSelectedOnly && !reg.selected) return false;
      if (filterTheme && reg.theme !== filterTheme) return false;
      if (filterRisk && reg.risk_level !== filterRisk) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return reg.name.toLowerCase().includes(search) ||
               reg.theme.toLowerCase().includes(search) ||
               reg.regulator?.toLowerCase().includes(search);
      }
      return true;
    });
  }, [universe?.regulations, showSelectedOnly, filterTheme, filterRisk, searchTerm]);

  // Stats
  const stats = useMemo(() => {
    if (!universe?.regulations) return { total: 0, selected: 0, high: 0, medium: 0, low: 0 };
    const regs = universe.regulations;
    return {
      total: regs.length,
      selected: regs.filter(r => r.selected).length,
      high: regs.filter(r => r.risk_level === 'High').length,
      medium: regs.filter(r => r.risk_level === 'Medium').length,
      low: regs.filter(r => r.risk_level === 'Low').length
    };
  }, [universe?.regulations]);

  const themes = useMemo(() => {
    if (!universe?.regulations) return [];
    return [...new Set(universe.regulations.map(r => r.theme))].sort();
  }, [universe?.regulations]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-gold font-mono">Loading universe...</div>
      </div>
    );
  }

  const existingNames = universe?.regulations?.map(r => r.name) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-heading font-semibold text-text-primary">
                  Regulatory Universe
                </h1>
                <VersionBadge version={universe?.version || 1} />
                <ApprovalBadge 
                  approved={universe?.approved} 
                  approvalDate={universe?.approval_date ? new Date(universe.approval_date).toLocaleDateString() : null}
                />
                {/* Version History Button */}
                <button
                  onClick={() => setShowVersionHistory(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-background-surface border border-border rounded-full text-sm text-text-tertiary hover:border-gold hover:text-gold transition-colors"
                  data-testid="show-version-history-btn"
                >
                  <Clock size={16} />
                  <span>History</span>
                </button>
              </div>
              <p className="text-sm text-text-tertiary">
                {universe?.approved 
                  ? `Approved by ${universe?.approved_by_name || 'Unknown'} via ${universe?.approving_committee || 'Committee'}`
                  : 'Review and validate the applicable regulations for your business'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/company-questionnaire')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-colors"
                data-testid="create-new-universe-btn"
              >
                <Plus size={18} />
                Create New Universe
              </button>
              <button
                onClick={() => setShowLegislationLibrary(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 text-gold rounded-lg hover:bg-gold/20 transition-colors"
                data-testid="browse-legislation-btn"
              >
                <Books size={18} />
                Browse Legislation
              </button>
              <button
                onClick={() => setShowFrameworksLibrary(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors"
                data-testid="browse-frameworks-btn"
              >
                <GitBranch size={18} />
                Browse Frameworks
              </button>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="flex items-center gap-6 py-3 border-t border-border/50">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-mono font-bold text-text-primary">{stats.total}</span>
              <span className="text-sm text-text-tertiary">Total</span>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="flex items-center gap-2">
              <span className="text-2xl font-mono font-bold text-gold">{stats.selected}</span>
              <span className="text-sm text-text-tertiary">Selected</span>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="font-mono text-sm text-text-primary">{stats.high}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="font-mono text-sm text-text-primary">{stats.medium}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="font-mono text-sm text-text-primary">{stats.low}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Instruction Banner - Show only if not approved */}
      {!universe?.approved && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 bg-gradient-to-r from-gold/10 via-gold/5 to-transparent border border-gold/20 rounded-lg"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                <Sparkle size={22} className="text-gold" weight="fill" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Your Auto-Generated Regulatory Universe
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed mb-3">
                  Based on your responses to our intake questionnaire, we've identified the regulations most likely applicable to your business. 
                  You can <span className="text-gold font-medium">edit risk ratings</span>, <span className="text-gold font-medium">remove</span> regulations that don't apply, 
                  or <span className="text-gold font-medium">approve</span> them to finalize your regulatory universe.
                </p>
                
                {/* Icon Legend - Larger and Clearer */}
                <div className="flex items-center gap-6 mb-3 p-4 bg-background rounded-lg border border-border">
                  <span className="text-sm text-text-tertiary font-medium">Quick Actions:</span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gold text-gold-foreground font-medium text-sm">
                      <PencilSimple size={16} weight="bold" />
                      <span>Edit</span>
                    </div>
                    <span className="text-text-tertiary">= Edit Risk Rating</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500 text-white font-medium text-sm">
                      <Trash size={16} weight="bold" />
                      <span>Remove</span>
                    </div>
                    <span className="text-text-tertiary">= Remove from Universe</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-6 text-sm">
                  <button
                    onClick={() => setShowLegislationLibrary(true)}
                    className="flex items-center gap-2 text-gold hover:underline"
                  >
                    <Books size={16} />
                    <span>Browse Legislation Library</span>
                    <ArrowRight size={14} />
                  </button>
                  <button
                    onClick={() => setShowFrameworksLibrary(true)}
                    className="flex items-center gap-2 text-emerald-400 hover:underline"
                  >
                    <GitBranch size={16} />
                    <span>Browse Frameworks Library</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search regulations..."
              className="w-full pl-10 pr-4 py-2.5 bg-background-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
            />
          </div>
          
          {/* Theme Filter */}
          <select
            value={filterTheme}
            onChange={(e) => setFilterTheme(e.target.value)}
            className="px-4 py-2.5 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
          >
            <option value="">All Themes</option>
            {themes.map(theme => (
              <option key={theme} value={theme}>{theme}</option>
            ))}
          </select>
          
          {/* Risk Filter */}
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
            className="px-4 py-2.5 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
          >
            <option value="">All Risk Levels</option>
            <option value="High">High Risk</option>
            <option value="Medium">Medium Risk</option>
            <option value="Low">Low Risk</option>
          </select>
          
          {/* Selected Only Toggle */}
          <button
            onClick={() => setShowSelectedOnly(!showSelectedOnly)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
              showSelectedOnly 
                ? 'bg-gold/10 border-gold text-gold' 
                : 'border-border text-text-tertiary hover:border-gold'
            }`}
          >
            <CheckCircle size={18} />
            Selected Only
          </button>
          
          {/* View Toggle */}
          <div className="flex items-center border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 ${viewMode === 'grid' ? 'bg-gold text-gold-foreground' : 'text-text-tertiary hover:bg-background-surface'}`}
            >
              <GridFour size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 ${viewMode === 'list' ? 'bg-gold text-gold-foreground' : 'text-text-tertiary hover:bg-background-surface'}`}
            >
              <ListBullets size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-32">
        <AnimatePresence mode="popLayout">
          {filteredRegulations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <FileText size={48} className="mx-auto mb-4 text-text-tertiary opacity-50" />
              <p className="text-text-tertiary">No regulations match your filters</p>
            </motion.div>
          ) : viewMode === 'grid' ? (
            <div className="grid lg:grid-cols-2 gap-4">
              {filteredRegulations.map((reg, index) => (
                <RegulationCard
                  key={reg.id}
                  reg={reg}
                  onToggle={handleToggleSelection}
                  onAdjust={setAdjustingReg}
                  onSelect={setSelectedReg}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRegulations.map((reg) => (
                <motion.div
                  key={reg.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`flex items-center gap-4 p-4 bg-background-surface border rounded-lg cursor-pointer transition-all ${
                    reg.selected ? 'border-gold/50' : 'border-border/50 opacity-70'
                  }`}
                  onClick={() => setSelectedReg(reg)}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleSelection(reg.id, reg.selected); }}
                    className={`p-2 rounded-lg border transition-all ${
                      reg.selected
                        ? 'bg-gold border-gold text-gold-foreground'
                        : 'border-border text-text-tertiary hover:border-gold'
                    }`}
                  >
                    {reg.selected ? <Check size={16} weight="bold" /> : <X size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-text-primary truncate">{reg.name}</h3>
                    <p className="text-xs text-text-tertiary">{reg.theme} • {reg.regulator}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <ScoreRing score={reg.risk_score} size={40} />
                    <RiskBadge level={reg.risk_level} />
                    <button
                      onClick={(e) => { e.stopPropagation(); setAdjustingReg(reg); }}
                      className="p-2 hover:bg-background rounded-lg text-text-tertiary hover:text-gold"
                    >
                      <PencilSimple size={16} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-md border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-6 py-3 bg-background-surface border border-border text-text-primary hover:border-gold rounded-lg transition-all"
            data-testid="universe-download-pdf-btn"
          >
            <DownloadSimple size={20} />
            Download PDF
          </button>
          
          {!universe?.approved && (
            <button
              onClick={() => setShowApproval(true)}
              className="flex items-center gap-2 px-8 py-3 bg-gold hover:bg-gold/90 text-gold-foreground rounded-lg font-medium transition-all"
              data-testid="universe-approve-btn"
            >
              <Check size={20} weight="bold" />
              Approve & Continue
            </button>
          )}
        </div>
      </div>

      {/* Modals */}
      <LegislationLibraryModal
        isOpen={showLegislationLibrary}
        onClose={() => setShowLegislationLibrary(false)}
        onAddLegislation={handleAddFromLibrary}
        existingNames={existingNames}
      />
      
      <FrameworksLibraryModal
        isOpen={showFrameworksLibrary}
        onClose={() => setShowFrameworksLibrary(false)}
        onAddFrameworks={handleAddFromLibrary}
        existingNames={existingNames}
      />
      
      <ApprovalModal
        isOpen={showApproval}
        onClose={() => setShowApproval(false)}
        onApprove={handleApprove}
      />
      
      <RiskAdjustmentModal
        regulation={adjustingReg}
        onClose={() => setAdjustingReg(null)}
        onSave={handleSaveAdjustment}
      />
      
      <AnimatePresence>
        {selectedReg && (
          <DetailPanel 
            regulation={selectedReg} 
            onClose={() => setSelectedReg(null)}
            onEditRating={setAdjustingReg}
            onRemove={handleRemoveRegulation}
            isApproved={universe?.approved}
          />
        )}
      </AnimatePresence>
      
      {/* Version History Panel */}
      <AnimatePresence>
        {showVersionHistory && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowVersionHistory(false)}
            />
            <VersionHistoryPanel 
              universe={universe}
              isOpen={showVersionHistory}
              onClose={() => setShowVersionHistory(false)}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegulatoryUniverse;
